import React from "react";
import { motion } from "framer-motion";
import { FaMusic, FaStar } from "react-icons/fa";

// Correct imports from src/images/landingPage
import landingImage from "../images/landingPage/landingImage.webp";
import landingImage768 from "../images/landingPage/landingImage-768.webp";
import landingImage480 from "../images/landingPage/landingImage-480.webp";

const stats = [
  { value: "10+", label: "Years Teaching" },
  { value: "1000+", label: "Students Trained" },
  { value: "6", label: "Instruments" },
];

const HeroSection = () => {
  return (
    <section className="relative flex min-h-[calc(100vh-4rem)] flex-col bg-white md:flex-row">
      {/* Left - Image */}
      <motion.div
        className="relative h-72 w-full overflow-hidden md:h-auto md:w-1/2"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <img
          src={landingImage}
          srcSet={`${landingImage480} 480w, ${landingImage768} 768w, ${landingImage} 1024w`}
          sizes="(max-width: 768px) 100vw, 50vw"
          alt="Students learning music at ABC School of Music"
          className="h-full w-full object-cover"
        />
        {/* subtle brand wash + right edge blend into text panel */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
        <div className="absolute inset-y-0 right-0 hidden w-24 bg-gradient-to-r from-transparent to-white md:block" />

        {/* Floating rating chip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="absolute bottom-5 left-5 flex items-center gap-3 rounded-2xl bg-white/90 px-4 py-3 shadow-card backdrop-blur"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white">
            <FaMusic />
          </div>
          <div>
            <div className="flex items-center gap-0.5 text-brand">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-xs" />
              ))}
            </div>
            <p className="text-xs font-medium text-ink-soft">
              Loved by our students
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Right - Content */}
      <motion.div
        className="flex w-full items-center justify-center px-6 py-12 md:w-1/2 md:px-16 md:py-0"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="max-w-xl text-center md:text-left"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <span className="eyebrow mb-6">Hyderabad's Home of Music</span>

          <h1 className="text-4xl font-extrabold leading-[1.1] text-ink md:text-6xl">
            Discover Your <span className="text-brand">Sound</span>
          </h1>

          <p className="mt-6 text-lg leading-relaxed text-ink-soft">
            At{" "}
            <span className="font-semibold text-ink">ABC School of Music</span>,
            we guide you on a personalized journey of rhythm, harmony, and
            creativity. Join the city's finest musicians and unleash your
            talent.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row md:items-start md:justify-start">
            <motion.a
              href="/contact-us"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="btn-primary w-full sm:w-auto"
            >
              Book a Free Trial
            </motion.a>
            <a href="/gallery" className="btn-secondary w-full sm:w-auto">
              Explore the Studio
            </a>
          </div>

          {/* Stats */}
          <div className="mt-12 grid max-w-md grid-cols-3 gap-4 border-t border-gray-200 pt-8">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                className="text-center md:text-left"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.5 }}
              >
                <p className="font-display text-2xl font-bold text-ink md:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 text-xs font-medium text-ink-soft sm:text-sm">
                  {s.label}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
